import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface BostonCelebrationOverlayProps {
  isOpen: boolean;
  winningTeam?: number;
  isUserTeam?: boolean;
  team1Names?: string;
  team2Names?: string;
  onClose?: () => void;
}

// Full-screen ambient golden particles raining down during Boston celebration (optimized for mobile WebKit)
const GOLD_STREAM_PARTICLES = [
  { x: '12%', delay: 0.1, size: 20, icon: '✦', color: '#FBBF24', duration: 3.2 },
  { x: '28%', delay: 0.8, size: 24, icon: '♠', color: '#F59E0B', duration: 3.6 },
  { x: '45%', delay: 0.3, size: 22, icon: '★', color: '#F59E0B', duration: 3.4 },
  { x: '60%', delay: 0.9, size: 24, icon: '♥', color: '#F87171', duration: 3.7 },
  { x: '75%', delay: 0.4, size: 22, icon: '✧', color: '#FDE047', duration: 3.2 },
  { x: '88%', delay: 1.1, size: 24, icon: '♣', color: '#F59E0B', duration: 3.8 },
];

// Firework epicenters distributed across key visual zones (reduced from 10 to 4 to protect mobile main thread)
const SCREEN_BURSTS = [
  { x: '22%', y: '20%', delay: 0.2, primaryColor: '#F59E0B' }, // Upper left (Warm gold)
  { x: '78%', y: '18%', delay: 0.6, primaryColor: '#FBBF24' }, // Upper right (Bright gold)
  { x: '20%', y: '45%', delay: 1.2, primaryColor: '#10B981' }, // Mid left (Emerald)
  { x: '80%', y: '42%', delay: 1.6, primaryColor: '#38BDF8' }, // Mid right (Ice platinum)
];

// Radiating particles for each firework burst (6-point radial star)
const FIREWORK_PARTICLES = [
  { dx: 0, dy: -46, icon: '✦', color: '#FBBF24', size: 18, rot: 45 },
  { dx: 42, dy: -24, icon: '✧', color: '#F59E0B', size: 16, rot: -30 },
  { dx: 42, dy: 24, icon: '✨', color: '#FFFFFF', size: 18, rot: 60 },
  { dx: 0, dy: 46, icon: '✧', color: '#10B981', size: 17, rot: -45 },
  { dx: -42, dy: 24, icon: '✦', color: '#FCD34D', size: 16, rot: 35 },
  { dx: -42, dy: -24, icon: '✨', color: '#FFFFFF', size: 18, rot: -60 },
];

// Floating ambient sparkles scattered across the screen
const AMBIENT_SCREEN_SPARKLES = [
  { x: '15%', y: '28%', icon: '✨', color: '#FDE047', size: 22, delay: 0.2 },
  { x: '85%', y: '24%', icon: '✨', color: '#38BDF8', size: 22, delay: 0.5 },
  { x: '50%', y: '18%', icon: '✦', color: '#F59E0B', size: 18, delay: 0.8 },
  { x: '25%', y: '68%', icon: '✦', color: '#FBBF24', size: 20, delay: 1.1 },
  { x: '75%', y: '65%', icon: '✧', color: '#FCD34D', size: 18, delay: 0.6 },
];

export const BostonCelebrationOverlay: React.FC<BostonCelebrationOverlayProps> = ({
  isOpen,
  isUserTeam = true,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5, ease: 'easeOut' } }}
          className="fixed inset-0 pointer-events-none z-[65] overflow-hidden select-none bg-transparent"
          aria-hidden="true"
        >
        {/* Full-screen rotating subtle sunburst light rays */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-[-50%] pointer-events-none opacity-15 flex items-center justify-center"
        >
          <div
            className={`w-full h-full ${
              isUserTeam
                ? 'bg-[conic-gradient(from_0deg_at_50%_50%,rgba(245,158,11,0.2)_0deg,transparent_20deg,rgba(245,158,11,0.2)_40deg,transparent_60deg,rgba(245,158,11,0.2)_80deg,transparent_100deg,rgba(245,158,11,0.2)_120deg,transparent_140deg,rgba(245,158,11,0.2)_160deg,transparent_180deg,rgba(245,158,11,0.2)_200deg,transparent_220deg,rgba(245,158,11,0.2)_240deg,transparent_260deg,rgba(245,158,11,0.2)_280deg,transparent_300deg,rgba(245,158,11,0.2)_320deg,transparent_340deg,rgba(245,158,11,0.2)_360deg)]'
                : 'bg-[conic-gradient(from_0deg_at_50%_50%,rgba(239,68,68,0.2)_0deg,transparent_20deg,rgba(239,68,68,0.2)_40deg,transparent_60deg,rgba(239,68,68,0.2)_80deg,transparent_100deg,rgba(239,68,68,0.2)_120deg,transparent_140deg,rgba(239,68,68,0.2)_160deg,transparent_180deg,rgba(239,68,68,0.2)_200deg,transparent_220deg,rgba(239,68,68,0.2)_240deg,transparent_260deg,rgba(239,68,68,0.2)_280deg,transparent_300deg,rgba(239,68,68,0.2)_320deg,transparent_340deg,rgba(239,68,68,0.2)_360deg)]'
            }`}
          />
        </motion.div>

        {/* Cascading Golden Rain / Card Suits / Sparks across the entire screen */}
        {GOLD_STREAM_PARTICLES.map((p, idx) => (
          <motion.div
            key={`gold-rain-${idx}`}
            initial={{ y: '-10vh', opacity: 0 }}
            animate={{
              y: ['-5vh', '105vh'],
              opacity: [0, 0.9, 0.9, 0],
              x: [0, idx % 2 === 0 ? 15 : -15, 0],
            }}
            transition={{
              duration: p.duration,
              repeat: Infinity,
              delay: p.delay,
              ease: 'linear',
            }}
            className="absolute top-0 pointer-events-none drop-shadow-[0_0_8px_rgba(245,158,11,0.8)] select-none transform-gpu"
            style={{
              left: p.x,
              fontSize: `${p.size}px`,
              color: isUserTeam ? p.color : (idx % 2 === 0 ? '#F87171' : '#FCA5A5'),
            }}
          >
            {p.icon}
          </motion.div>
        ))}

        {/* Firework bursts popping across the screen */}
        {SCREEN_BURSTS.map((burst, bIdx) => (
          <div
            key={`burst-${bIdx}`}
            className="absolute pointer-events-none bg-transparent -translate-x-1/2 -translate-y-1/2 transform-gpu"
            style={{ left: burst.x, top: burst.y }}
          >
            {/* Expanding shockwave ring */}
            <motion.div
              initial={{ scale: 0.1, opacity: 0 }}
              animate={{
                scale: [0.1, 1.4, 1.7],
                opacity: [0, 0.85, 0],
              }}
              transition={{
                duration: 0.9,
                delay: burst.delay,
                ease: 'easeOut',
              }}
              className="absolute -inset-10 rounded-full border-2 pointer-events-none bg-transparent transform-gpu"
              style={{
                borderColor: isUserTeam ? `${burst.primaryColor}99` : '#EF444499',
              }}
            />

            {/* Radiating firework particles */}
            {FIREWORK_PARTICLES.map((p, pIdx) => (
              <motion.div
                key={`p-${bIdx}-${pIdx}`}
                initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
                animate={{
                  x: [0, p.dx * 1.3, p.dx * 1.8],
                  y: [0, p.dy * 1.3, p.dy * 1.8 + 10],
                  scale: [0, 1.35, 0.4],
                  opacity: [0, 1, 0],
                  rotate: [0, p.rot],
                }}
                transition={{
                  duration: 1.3,
                  delay: burst.delay + (pIdx * 0.02),
                  ease: 'easeOut',
                }}
                className="absolute text-center leading-none pointer-events-none select-none bg-transparent drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] transform-gpu"
                style={{
                  color: isUserTeam ? p.color : '#FCA5A5',
                  fontSize: `${p.size}px`,
                }}
              >
                {p.icon}
              </motion.div>
            ))}
          </div>
        ))}

        {/* Ambient twinkling sparkles across the screen */}
        {AMBIENT_SCREEN_SPARKLES.map((sparkle, sIdx) => (
          <motion.div
            key={`sparkle-${sIdx}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [0, 1.3, 0.8, 1.2, 0],
              opacity: [0, 1, 0.7, 0.9, 0],
              y: [0, -8, -16, -24, -32],
            }}
            transition={{
              duration: 2.6,
              delay: sparkle.delay,
              ease: 'easeInOut',
            }}
            className="absolute pointer-events-none select-none bg-transparent leading-none drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] -translate-x-1/2 -translate-y-1/2 transform-gpu"
            style={{
              left: sparkle.x,
              top: sparkle.y,
              color: isUserTeam ? sparkle.color : '#FCA5A5',
              fontSize: `${sparkle.size}px`,
            }}
          >
            {sparkle.icon}
          </motion.div>
        ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
